import {DOM} from "../../../../../public/js/utils/DOM";
import flatpickr from "flatpickr";
import {Options} from "flatpickr/dist/types/options";
import { Czech } from "flatpickr/dist/l10n/cs.js"
import {Instance} from "flatpickr/dist/types/instance";

export namespace ControlsFormDateTimePicker {

    class Config {
        public static readonly inputContainerClass = "DateTimePicker";
        public static readonly minDataName = "min";
        public static readonly maxDataName = "max";
        public static readonly timeEnabledDataName = "timeEnabled"
        public static readonly closeButtonElementClass = "DateTimePicker-clear";
        public static readonly inputElementClassName = "DateTimePicker-input";
        public static readonly inputGeneratedClass = "DateTimePicker-generated";
    }

    export class Control {

        /**
         * Main initialization function
         * @param container - form container element
         */
        public static initialize(container?: HTMLElement) : void {

            const inputs = DOM.Parser.getHtmlElements(Config.inputContainerClass, container, true) as HTMLCollectionOf<HTMLDivElement>;
            for (let i = 0; i < inputs.length; i++) {
                this.initializeElement(inputs[i]);
            }
        }

        /**
         * Initialize concrete element
         * @param element - input container main element
         * @private
         */
        private static initializeElement(element: HTMLDivElement) : void {

            // Init data
            const input = DOM.Parser.getHtmlElementByClass(Config.inputElementClassName, element) as HTMLInputElement;
            const defaultValue = input.value && input.value.trim().length > 0 ? (parseInt(input.value.trim()) * 1000) : undefined;
            const timeEnabled = DOM.Parser.getDataByNameFromElement(Config.timeEnabledDataName, element) === 'yes';

            // Create options
            let options : Options = {
                disableMobile: true,
                dateFormat: timeEnabled ? 'd-m-Y H:i:S' : 'd-m-Y',
                altFormat: timeEnabled ? 'j. n. Y H:i:S' : 'j. n. Y', // todo localization
                altInput: true,
                altInputClass: Config.inputGeneratedClass,
                enableTime: timeEnabled,
                enableSeconds: timeEnabled,
                time_24hr: timeEnabled,
                defaultDate: defaultValue,
                locale: DOM.Parser.getLocale() === 'cs' ? Czech : undefined,
                allowInput: true // Allow direct input to input field
            };

            // Min date
            const minDate = DOM.Parser.getDataByNameFromElementOrNull(Config.minDataName, element as HTMLElement);
            if (minDate) {
                let minDateObject = new Date(parseInt(minDate) * 1000).getTime();
                options.minDate = (defaultValue && defaultValue < minDateObject) ? defaultValue : minDateObject;
            }

            // Max date
            const maxDate = DOM.Parser.getDataByNameFromElementOrNull(Config.maxDataName, element as HTMLElement);
            if (maxDate) { options.maxDate = new Date(parseInt(maxDate) * 1000).getTime(); }

            // Init instance
            const flatpickrInstance = flatpickr(input, options) as Instance;
            this.triggerClear(element, flatpickrInstance, input);
            this.triggerLabel(element, input);
        }

        /**
         * Trigger label activation
         * @param element - container
         * @param input - input (not generated)
         * @private
         */
        private static triggerLabel(element: HTMLElement, input: HTMLInputElement) : void {

            // Get elements
            const generatedInput = DOM.Parser.getHtmlElementByClass(Config.inputGeneratedClass, element) as HTMLInputElement;
            const label = DOM.Parser.getElementByTag('label', DOM.Parser.getParentElement(element));

            // Focus - activate label
            generatedInput.addEventListener('focus', () => { label.classList.add('active'); });

            // Blur - remove active if empty
            generatedInput.addEventListener('blur', () => {
                if (input.value.trim().length === 0) {
                    label.classList.remove('active');
                }
            });

            // Update on init
            if (input.value.trim().length > 0) {
                label.classList.add('active');
            }
        }

        /**
         * Clear button trigger
         * @param element - container
         * @param instance - instance of FP
         * @param input - not generated input
         * @private
         */
        private static triggerClear(element: HTMLElement, instance: Instance, input: HTMLInputElement) : void {

            // Get elements
            const closeButton = DOM.Parser.getHtmlElementByClass(Config.closeButtonElementClass, element) as HTMLEmbedElement;
            const label = DOM.Parser.getElementByTag('label', DOM.Parser.getParentElement(element));

            // Clear functionality
            closeButton.addEventListener("click", () => {
                instance.clear();
                label.classList.remove('active');
            });

            // Update clear visibility on change
            input.addEventListener('change', () => {
                if (input.value.trim().length === 0) {
                    element.classList.add('empty');
                    label.classList.remove('active');
                } else {
                    element.classList.remove('empty');
                    label.classList.add('active');
                }
            })
        }
    }
}